import { useState } from "react";
import { BookOpen, Check, ChevronRight, RotateCcw, Trophy, Volume2 } from "lucide-react";

const passages = [
  {
    id: 1,
    level: "beginner",
    title: "My Daily Routine",
    text: "My name is Amit. I wake up at 6 AM every day. I brush my teeth and take a shower. Then I eat breakfast with my family. I usually have bread and tea. After breakfast, I go to school by bus. School starts at 8 AM. I study English, Math, and Science. I come home at 3 PM. In the evening, I play cricket with my friends. I eat dinner at 8 PM and go to bed at 10 PM.",
    questions: [
      { question: "What time does Amit wake up?", options: ["5 AM", "6 AM", "7 AM", "8 AM"], answer: "6 AM" },
      { question: "What does Amit usually have for breakfast?", options: ["Rice and curry", "Bread and tea", "Eggs and juice", "Cereal and milk"], answer: "Bread and tea" },
      { question: "How does Amit go to school?", options: ["By car", "By walking", "By bus", "By train"], answer: "By bus" },
      { question: "What does Amit do in the evening?", options: ["Studies", "Watches TV", "Plays cricket", "Sleeps"], answer: "Plays cricket" }
    ]
  },
  {
    id: 2,
    level: "beginner",
    title: "The Park",
    text: "There is a beautiful park near my house. It has many green trees and colorful flowers. Every morning, people come to the park for walking and exercise. Children play on the swings and slides. There is a small pond with fish in it. Old people sit on the benches and talk to each other. On weekends, the park is very crowded. My favorite time to visit is early morning when the air is fresh and the birds are singing.",
    questions: [
      { question: "Where is the park?", options: ["Far from the city", "Near the house", "Next to school", "In the market"], answer: "Near the house" },
      { question: "What is in the pond?", options: ["Ducks", "Frogs", "Fish", "Nothing"], answer: "Fish" },
      { question: "When is the park crowded?", options: ["Weekdays", "Weekends", "Mornings", "Nights"], answer: "Weekends" },
      { question: "What is the narrator's favorite time to visit?", options: ["Evening", "Afternoon", "Night", "Early morning"], answer: "Early morning" }
    ]
  },
  {
    id: 3,
    level: "intermediate",
    title: "A Letter to a Friend",
    text: "Dear Priya, I hope you are doing well. I am writing to tell you about my new job. I joined a software company last month. The office is in the city center, and it takes me about 30 minutes to reach by metro. My colleagues are very friendly and helpful. I work from 9 AM to 6 PM, Monday to Friday. The best part is that I get to learn new technologies every day. However, I sometimes miss our college days when we used to spend time together in the library. I would love to meet you soon. Please let me know when you are free. Take care, Rahul.",
    questions: [
      { question: "What kind of company does Rahul work at?", options: ["Marketing", "Software", "Banking", "Teaching"], answer: "Software" },
      { question: "How does Rahul travel to office?", options: ["By bus", "By car", "By metro", "Walking"], answer: "By metro" },
      { question: "What does Rahul miss?", options: ["His old job", "College days", "His family", "Holidays"], answer: "College days" },
      { question: "What is the best part of Rahul's job?", options: ["High salary", "Free food", "Learning new technologies", "Short hours"], answer: "Learning new technologies" }
    ]
  },
  {
    id: 4,
    level: "advanced",
    title: "The Impact of Social Media",
    text: "Social media has transformed the way we communicate and share information. Platforms like Instagram, Twitter, and YouTube have given everyone a voice. People can now connect with others across the globe instantly. However, there are growing concerns about the negative effects of social media. Studies have shown that excessive use can lead to anxiety, depression, and poor sleep quality. Cyberbullying has become a serious issue, especially among teenagers. Additionally, the spread of misinformation through social media has created challenges for society. While social media offers many benefits, it is important to use it responsibly and maintain a healthy balance between online and offline life.",
    questions: [
      { question: "What has social media transformed?", options: ["Education", "Communication and sharing", "Transportation", "Healthcare"], answer: "Communication and sharing" },
      { question: "What can excessive social media use cause?", options: ["Better sleep", "Anxiety and depression", "Weight gain", "Improved focus"], answer: "Anxiety and depression" },
      { question: "What is a serious issue among teenagers?", options: ["Homework", "Cyberbullying", "Social media addiction", "Lack of friends"], answer: "Cyberbullying" },
      { question: "What is the main message of the passage?", options: ["Avoid social media", "Use it responsibly", "Only use YouTube", "Social media is bad"], answer: "Use it responsibly" }
    ]
  }
];

function speak(text) {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.8;
    u.lang = "en-US";
    window.speechSynthesis.speak(u);
  }
}

export default function Reading() {
  const [currentPassage, setCurrentPassage] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  function selectAnswer(qIdx, option) {
    if (submitted) return;
    setAnswers({ ...answers, [qIdx]: option });
  }

  function handleSubmit() {
    if (!currentPassage) return;
    if (Object.keys(answers).length < currentPassage.questions.length) return;
    setSubmitted(true);
  }

  function handleReset() {
    setAnswers({});
    setSubmitted(false);
  }

  function goBack() {
    setCurrentPassage(null);
    setAnswers({});
    setSubmitted(false);
  }

  const score = submitted && currentPassage
    ? currentPassage.questions.filter((q, i) => answers[i] === q.answer).length
    : 0;

  // Passage list
  if (!currentPassage) {
    return (
      <div className="space-y-6 page-enter relative">
        <div className="blob blob-emerald" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
        <div className="blob blob-cyan" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
        <div className="relative">
          <h1 className="font-display text-4xl md:text-5xl font-normal mb-2"><em>Reading.</em></h1>
          <p className="text-gray-600 dark:text-gray-300 font-semibold">Read short passages and answer questions to test your understanding.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {passages.map((p) => (
            <button
              key={p.id}
              onClick={() => setCurrentPassage(p)}
              className="bg-white rounded-xl shadow-sm p-6 border-2 border-gray-100 hover:border-gold-500 hover:shadow-md text-left transition-all group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-5 h-5 text-gold-700" />
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      p.level === "beginner" ? "bg-sage-light text-sage-dark" : p.level === "intermediate" ? "bg-gold-100 text-gold-700" : "bg-berry-light text-berry"
                    }`}>{p.level}</span>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-1">{p.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{p.text.substring(0, 100)}...</p>
                  <p className="text-xs text-gray-400 mt-2">{p.questions.length} questions</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gold-700 transition-colors flex-shrink-0" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Active passage
  return (
    <div className="space-y-6">
      <button onClick={goBack} className="text-gold-700 hover:underline text-sm">&larr; Back to all passages</button>

      {/* Passage */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            currentPassage.level === "beginner" ? "bg-sage-light text-sage-dark" : currentPassage.level === "intermediate" ? "bg-gold-100 text-gold-700" : "bg-berry-light text-berry"
          }`}>{currentPassage.level}</span>
          <button onClick={() => speak(currentPassage.text)} className="inline-flex items-center gap-1.5 text-sm text-gold-700 hover:text-gold-700">
            <Volume2 className="w-4 h-4" /> Listen
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{currentPassage.title}</h2>
        <div className="bg-gray-50 rounded-xl p-6">
          <p className="text-gray-700 leading-relaxed text-lg">{currentPassage.text}</p>
        </div>
      </div>

      {/* Questions */}
      {currentPassage.questions.map((q, qIdx) => {
        const userAnswer = answers[qIdx];
        const isCorrect = userAnswer === q.answer;
        return (
          <div key={qIdx} className="bg-white rounded-2xl shadow-lg p-6">
            <p className="text-sm text-gray-400 mb-2">Question {qIdx + 1}</p>
            <h3 className="text-lg font-bold text-gray-800 mb-4">{q.question}</h3>
            <div className="grid gap-2">
              {q.options.map((option) => {
                let style = "border-2 border-gray-200 bg-white text-gray-800 hover:border-gold-500 hover:bg-paper-deep";
                if (userAnswer === option && !submitted) style = "border-2 border-gold-500 bg-paper-deep text-gold-700";
                if (submitted) {
                  if (option === q.answer) style = "border-2 border-sage bg-sage-light text-sage-dark";
                  else if (userAnswer === option && !isCorrect) style = "border-2 border-berry-light bg-berry-light text-berry-dark";
                  else style = "border-2 border-gray-200 bg-gray-50 text-gray-400";
                }
                return (
                  <button key={option} onClick={() => selectAnswer(qIdx, option)} className={`p-3 rounded-xl font-medium text-left transition-all ${style} ${submitted ? "cursor-default" : "cursor-pointer"}`}>
                    {option}
                  </button>
                );
              })}
            </div>
            {submitted && !isCorrect && (
              <p className="text-sm text-berry mt-2">Correct answer: <strong>{q.answer}</strong></p>
            )}
          </div>
        );
      })}

      {/* Submit / Score */}
      {!submitted ? (
        <button onClick={handleSubmit} disabled={Object.keys(answers).length < currentPassage.questions.length} className="inline-flex items-center gap-2 bg-sage text-white px-6 py-3 rounded-xl font-semibold hover:bg-sage transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          <Check className="w-5 h-5" /> Submit Answers
        </button>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <Trophy className="w-10 h-10 text-gold-700 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Score: {score} / {currentPassage.questions.length}</h2>
          <p className="text-gray-500 mb-6">
            {score === currentPassage.questions.length ? "Perfect! You understood everything!" : score >= currentPassage.questions.length / 2 ? "Good reading! Review the passage for missed answers." : "Read the passage again carefully and try once more."}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={handleReset} className="inline-flex items-center gap-2 bg-ink-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">
              <RotateCcw className="w-4 h-4" /> Retry
            </button>
            <button onClick={goBack} className="inline-flex items-center gap-2 border-2 border-gold-500 text-gold-700 px-6 py-3 rounded-xl font-semibold hover:bg-paper-deep transition-all">
              All Passages
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
